import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { TextField } from '@dsny/dsny-component-library'
import {
  Container,
  FormWrapper,
  Title,
  StyledCheckbox,
  Row,
  CheckboxLabel,
  FullWidthButton,
  FooterText,
  SignUpLink
} from './AuthForm.styles'
import { magicLinkSchema } from '../authSchema'
import SuccessPage from './SuccessPage/SuccessPage'
import { Content } from './SuccessPage/SuccessPage.styles'

export default function MagicLinkForm({ onSubmit, setIsMagicLinkFlow }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const [publicDevice, setPublicDevice] = useState(false)
  const [isMagicLinkSent, setIsMagicLinkSent] = useState(false)
  const [isMagicLinkResent, setIsMagicLinkResent] = useState(false)

  const {
    setValue,
    handleSubmit,
    register,
    getValues,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(magicLinkSchema)
  })

  const handleCheckBoxChange = () => {
    setValue('publicDevice', !publicDevice)
    setPublicDevice(!publicDevice)
  }

  const onFormSubmit = async (values, e) => {
    try {
      await onSubmit(values, e)
      setIsMagicLinkSent(true)
    } catch (error) {
      console.error(error?.data)
    }
  }

  const handleResendRequest = async () => {
    setIsMagicLinkResent(true)
    await onFormSubmit(getValues())
  }

  if (isMagicLinkSent) {
    const email = getValues('email')

    return (
      <SuccessPage
        title={t('login.we-sent-you-an-email')}
        returnSignInAction={() => setIsMagicLinkFlow(false)}
        onSubmit={handleResendRequest}
        isMagicLinkResent={isMagicLinkResent}
      >
        <Content>
          {t('login.magic-link-sent-to')}{' '}
          <strong style={{ color: theme.colors.neutralW70 }}>[{email}]</strong>{' '}
        </Content>

        <Content>{t('login.magic-link-not-receieved-disclaimer')}</Content>
      </SuccessPage>
    )
  }

  return (
    <Container>
      <FormWrapper>
        <Title>{t('login.login-header-title')}</Title>

        <form onSubmit={handleSubmit((values, e) => onFormSubmit(values, e))}>
          <TextField
            id="email"
            name="email"
            required
            {...register('email')}
            setting="player"
            ref={null}
            inputRef={register('email').ref}
            wrapperStyles={{ paddingRight: 0, marginBottom: 12 }}
            labelText={t('signup.email')}
            error={errors?.email?.message}
            highlightColor={theme.colors.neutralW40}
            inputStyle={{
              height: 44,
              borderColor: errors?.email
                ? theme.colors.error70
                : theme.colors.neutralW50
            }}
            errorStyle={{ color: theme.colors.error70, marginTop: 8 }}
          />

          <Row style={{ marginBottom: 24 }}>
            <div>
              <StyledCheckbox
                id="publicDevice"
                name="publicDevice"
                {...register('publicDevice')}
                checked={publicDevice}
                onClick={handleCheckBoxChange}
                style={{
                  marginRight: 3,
                  background: publicDevice
                    ? theme.colors.interact70
                    : theme.colors.neutralW20
                }}
              />
              <CheckboxLabel htmlFor="publicDevice">
                {t('login.public-computer')}
              </CheckboxLabel>
            </div>
          </Row>

          <FullWidthButton type="submit" color="primary" setting="player">
            {t('login.send-magic-link')}
          </FullWidthButton>

          <FullWidthButton
            setting="player"
            variant="secondary"
            style={{ marginTop: 16 }}
            onClick={() => setIsMagicLinkFlow(false)}
          >
            {t('login.sign-in-with-password')}
          </FullWidthButton>
        </form>

        <FooterText>
          {t('login.dont-have-an-account')}
          <SignUpLink onClick={() => navigate('/signup')}>
            {t('signup.sign-up')}
          </SignUpLink>
        </FooterText>
      </FormWrapper>
    </Container>
  )
}
